/**
 * Mijn pagina (SPEC §6.1): wat een held zelf ziet en kan.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiFout, type Heldstand, type OmgevingInfo } from '../api';
import { Bevestigknop, Datum, Euro, Knop, Kroon, Laden, Melding, Niveaubadge, Veld, useTitel } from '../componenten/basis';
import { Logokiezer } from '../componenten/Logokiezer';
import { Pagina } from '../componenten/Schil';
import { MAXIMUM_INCASSO_CENT, alsEuro, uitInvoer } from '../lib/geld';
import { NIVEAUNAMEN, NIVEAU_BIJVOEGLIJK } from '../lib/niveau';
import { STATUSNAMEN } from '../lib/status';
import { BIJDRAGESTATUSNAMEN, SOORTNAMEN, WIJZENAMEN, type Logo } from '../lib/vormen';
import { Inlogformulier } from './Inloggen';

export function Mijn({ omgeving, ververs }: { omgeving: OmgevingInfo; ververs: () => Promise<void> }) {
  useTitel('Mijn pagina');
  const [stand, zetStand] = useState<Heldstand | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [nietIngelogd, zetNietIngelogd] = useState(!omgeving.heldIngelogd);

  const laad = useCallback(async () => {
    try {
      zetStand(await api.haal<Heldstand>('/api/mijn'));
      zetNietIngelogd(false);
    } catch (f) {
      if (f instanceof ApiFout && f.status === 401) zetNietIngelogd(true);
      else zetFout((f as Error).message);
    }
  }, []);

  useEffect(() => {
    void laad();
  }, [laad]);

  // Kwam de bezoeker via de knop in een mail, dan staat erbij voor wie die was.
  const [params] = useSearchParams();
  const voor = params.get('voor');
  const [linkhouder, zetLinkhouder] = useState<Linkhouder | null>(null);
  useEffect(() => {
    if (!voor) return;
    api.haal<Linkhouder>(`/api/link-voor/${encodeURIComponent(voor)}`).then(zetLinkhouder).catch(() => zetLinkhouder(null));
  }, [voor, nietIngelogd]);

  if (nietIngelogd) {
    return (
      <Pagina smal>
        <div className="verschijn py-16">
          {voor && linkhouder && (
            <div className="mb-6">
              <LinkVoorMelding voor={voor} linkhouder={linkhouder} ingelogdAls={null} />
            </div>
          )}
          <Inlogformulier soort="held" omgeving={omgeving} />
        </div>
      </Pagina>
    );
  }

  return (
    <Pagina smal>
      <div className="verschijn py-10 sm:py-16">
        {voor && linkhouder && stand && !linkhouder.ditBenJij && (
          <div className="mb-6">
            <LinkVoorMelding
              voor={voor}
              linkhouder={linkhouder}
              ingelogdAls={stand.weergavenaam}
              onUitloggen={async () => {
                await api.stuur('/api/uitloggen', { soort: 'held' });
                await ververs();
                zetStand(null);
                zetNietIngelogd(true);
              }}
            />
          </div>
        )}
        {fout && <Melding soort="fout">{fout}</Melding>}
        {!stand && !fout && <Laden />}
        {stand && <Heldpagina stand={stand} zetStand={zetStand} ververs={ververs} />}
      </div>
    </Pagina>
  );
}

interface Linkhouder {
  naam: string;
  ditBenJij: boolean;
}

/**
 * De link uit een mail hoort bij een andere held dan wie hier is ingelogd (of
 * er is niemand ingelogd): zeg dat, en bied een inloglink voor die held aan.
 * Die gaat naar het adres van de held zelf.
 */
function LinkVoorMelding({
  voor,
  linkhouder,
  ingelogdAls,
  onUitloggen,
}: {
  voor: string;
  linkhouder: Linkhouder;
  ingelogdAls: string | null;
  onUitloggen?: () => Promise<void>;
}) {
  const [verstuurd, zetVerstuurd] = useState<string | null>(null);
  const [bezig, zetBezig] = useState(false);

  async function stuurLink() {
    zetBezig(true);
    try {
      const uit = await api.stuur<{ melding: string }>(`/api/link-voor/${encodeURIComponent(voor)}`);
      zetVerstuurd(uit.melding);
    } catch (f) {
      zetVerstuurd((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }

  return (
    <div className="kaart p-5">
      <p className="text-sm">
        {ingelogdAls ? (
          <>
            Je bent ingelogd als <strong>{ingelogdAls}</strong>. De link waarmee je hier kwam hoort bij <strong>{linkhouder.naam}</strong>.
          </>
        ) : (
          <>
            De link waarmee je hier kwam hoort bij <strong>{linkhouder.naam}</strong>. Vraag een inloglink aan; die gaat naar het e-mailadres van {linkhouder.naam}.
          </>
        )}
      </p>
      {verstuurd ? (
        <p className="mt-3 text-sm text-inkt-zacht">{verstuurd}</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Knop klein onClick={stuurLink} bezig={bezig}>
            Stuur een inloglink voor {linkhouder.naam}
          </Knop>
          {onUitloggen && (
            <Knop soort="stil" klein onClick={onUitloggen}>
              Uitloggen
            </Knop>
          )}
        </div>
      )}
    </div>
  );
}

function Heldpagina({ stand, zetStand, ververs }: { stand: Heldstand; zetStand: (s: Heldstand) => void; ververs: () => Promise<void> }) {
  const [melding, zetMelding] = useState<string | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const bedrijf = stand.soort === 'bedrijf';

  async function uitloggen() {
    await api.stuur('/api/uitloggen', { soort: 'held' });
    await ververs();
    window.location.href = '/';
  }

  async function opzeggen() {
    try {
      zetStand(await api.stuur<Heldstand>('/api/mijn/opzeggen', { reden: null }));
      zetMelding('Je bent afgemeld. Dank je wel voor de tijd dat je held was.');
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  async function afkopen(niveau: string) {
    try {
      const uit = await api.stuur<{ checkoutUrl: string }>('/api/mijn/afkoop', { niveau });
      window.location.href = uit.checkoutUrl;
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-goud-500">Mijn pagina</p>
          <h1 className="mt-2 text-4xl">Hallo, {stand.naam.split(' ')[0]}.</h1>
        </div>
        <Knop soort="stil" klein onClick={uitloggen}>
          Uitloggen
        </Knop>
      </div>

      {melding && <div className="mt-6"><Melding soort="goed">{melding}</Melding></div>}
      {fout && <div className="mt-6"><Melding soort="fout">{fout}</Melding></div>}

      <section className="kaart mt-8 p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Kroon className={`h-12 w-12 ${stand.niveau === 'goud' ? 'text-goud' : stand.niveau === 'zilver' ? 'text-zilver' : 'text-brons'}`} />
            <div>
              <p className="font-display text-2xl">
                {hoofdletter(NIVEAU_BIJVOEGLIJK[stand.niveau])} held{bedrijf ? ` · ${stand.weergavenaam}` : ''}
              </p>
              <p className="text-sm text-inkt-zacht">
                {stand.status === 'actief' && stand.lid_sinds && <>Sinds <Datum waarde={stand.lid_sinds} /> · {stand.betaaldeJaren} {stand.betaaldeJaren === 1 ? 'jaarbijdrage' : 'jaarbijdragen'} voldaan</>}
                {stand.status !== 'actief' && <>Status: {STATUSNAMEN[stand.status]}</>}
              </p>
            </div>
          </div>
          <Niveaubadge niveau={stand.niveau} groot />
        </div>

        {stand.jubileum && (
          <div className="mt-5">
            <Melding soort="goed">Dit jaar ben je {stand.jubileum} jaar held. Dat staat bij je naam op de muur.</Melding>
          </div>
        )}

        {stand.status === 'actief' && (
          <dl className="mt-6 grid gap-4 border-t border-lijn-zacht pt-6 text-sm sm:grid-cols-3">
            {stand.volgend && (
              <div>
                <dt className="etiket">Volgende niveau</dt>
                <dd>
                  {NIVEAUNAMEN[stand.volgend.niveau]} over {stand.volgend.overJaren} {stand.volgend.overJaren === 1 ? 'jaarbijdrage' : 'jaarbijdragen'}
                </dd>
              </div>
            )}
            {stand.volgendeIncasso && (
              <div>
                <dt className="etiket">Volgende bijdrage</dt>
                <dd>
                  <Euro cent={stand.volgendeIncasso.bedrag_cent} /> rond <Datum waarde={stand.volgendeIncasso.datum} />
                </dd>
              </div>
            )}
            <div>
              <dt className="etiket">Machtiging</dt>
              <dd>
                {stand.mandaat ? (
                  <>Rekening eindigend op {stand.mandaat.laatste4 ?? '…'}{stand.mandaat.tenaamstelling ? `, ${stand.mandaat.tenaamstelling}` : ''}</>
                ) : (
                  <span className="text-inkt-zacht">Geen - je krijgt jaarlijks een betaalverzoek</span>
                )}
              </dd>
            </div>
          </dl>
        )}

        {stand.status === 'verlopen' && (
          <div className="mt-6">
            <Melding>Er staat een bijdrage open, en daarom sta je even niet op de muur. Betaal hem hieronder en je staat er weer, met al je jaren.</Melding>
          </div>
        )}
        {stand.status === 'opgezegd' && (
          <div className="mt-6">
            <Melding>Je hebt opgezegd. Terugkomen kan altijd: meld je opnieuw aan via “Word held” met hetzelfde e-mailadres.</Melding>
          </div>
        )}
        {stand.status === 'aangemeld' && (
          <div className="mt-6">
            <Melding>Je aanmelding is binnen, maar de eerste betaling nog niet. Zodra die er is sta je op de muur.</Melding>
          </div>
        )}
      </section>

      {stand.afkoop.length > 0 && (
        <section className="kaart mt-4 p-6 sm:p-8">
          <h2 className="text-2xl">Niet wachten op {stand.volgend ? NIVEAUNAMEN[stand.volgend.niveau].toLowerCase() : 'meer'}?</h2>
          <p className="mt-2 text-sm text-inkt-zacht">Koop een hoger niveau in één keer af: je bent dat niveau dan meteen, zolang je held bent. Het is geen vooruitbetaling; je jaarbijdrage loopt gewoon door.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {stand.afkoop.map((a) => (
              <Knop key={a.niveau} soort="stil" onClick={() => afkopen(a.niveau)}>
                {NIVEAUNAMEN[a.niveau]} voor <Euro cent={a.bedrag_cent} />
              </Knop>
            ))}
          </div>
        </section>
      )}

      <Gegevensformulier stand={stand} zetStand={zetStand} />

      <section className="kaart mt-4 p-6 sm:p-8">
        <h2 className="text-2xl">Je bijdragen</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="tabel">
            <thead>
              <tr>
                <th>Wat</th>
                <th>Jaar</th>
                <th>Bedrag</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stand.bijdragen
                .filter((b) => !(b.soort !== 'jaarbijdrage' && b.status === 'verlopen'))
                .map((b) => (
                <tr key={b.id}>
                  <td>{SOORTNAMEN[b.soort]}</td>
                  <td className="text-inkt-zacht">{b.kalenderjaar ?? '-'}</td>
                  <td><Euro cent={b.bedrag_cent} /></td>
                  <td>
                    {BIJDRAGESTATUSNAMEN[b.status]}
                    {b.wijze && b.status === 'betaald' && <span className="text-inkt-zacht"> · {WIJZENAMEN[b.wijze]}</span>}
                    {(b.status === 'open' || b.status === 'mislukt') && b.betaal_token && stand.status !== 'opgezegd' && (
                      <> · <a href={`/betalen/${b.betaal_token}`}>betalen</a></>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(stand.status === 'actief' || stand.status === 'verlopen') && (
        <section className="mt-10 border-t border-lijn-zacht pt-6 text-sm text-inkt-zacht">
          <p>Wil je stoppen? Dat kan per direct. Je naam gaat van de muur en we schrijven niets meer af. Over het lopende jaar vindt geen restitutie plaats.</p>
          <div className="mt-3">
            <Bevestigknop vraag="Weet je het zeker?" onBevestig={opzeggen}>
              Opzeggen
            </Bevestigknop>
          </div>
        </section>
      )}
    </>
  );
}

function hoofdletter(woord: string): string {
  return woord.charAt(0).toUpperCase() + woord.slice(1);
}

function Gegevensformulier({ stand, zetStand }: { stand: Heldstand; zetStand: (s: Heldstand) => void }) {
  const bedrijf = stand.soort === 'bedrijf';
  const [invoer, zetInvoer] = useState({
    naam: stand.naam,
    weergavenaam: stand.weergavenaam,
    regel: stand.regel ?? '',
    website: stand.website ?? '',
    telefoon: stand.telefoon ?? '',
    toon_op_muur: stand.toon_op_muur,
  });
  const [bedragTekst, zetBedragTekst] = useState(stand.jaarbijdrage_cent ? alsEuro(stand.jaarbijdrage_cent).replace('€ ', '').replace(',-', '') : '');
  const [logo, zetLogo] = useState<Logo | null | undefined>(undefined);
  const [bezig, zetBezig] = useState(false);
  const [melding, zetMelding] = useState<string | null>(null);

  const eigen = bedragTekst.trim() ? uitInvoer(bedragTekst) : null;
  const bedragFout = !bedragTekst.trim()
    ? null
    : eigen === null || eigen < stand.tarief_cent
      ? `Minimaal ${alsEuro(stand.tarief_cent)}`
      : eigen > MAXIMUM_INCASSO_CENT
        ? `Hooguit ${alsEuro(MAXIMUM_INCASSO_CENT)}. Wil je meer geven? Mail helden@hilfertheem.nl.`
        : null;

  async function bewaar(e: FormEvent) {
    e.preventDefault();
    if (bedragFout) return;
    zetBezig(true);
    zetMelding(null);
    try {
      let nieuw = await api.stuur<Heldstand>('/api/mijn', { ...invoer, jaarbijdrage_cent: eigen && eigen > stand.tarief_cent ? eigen : null }, 'PUT');
      if (logo !== undefined) {
        nieuw = await api.stuur<Heldstand>('/api/mijn/logo', { logo }, 'PUT');
        zetLogo(undefined);
      }
      zetStand(nieuw);
      zetMelding('Opgeslagen.');
    } catch (f) {
      zetMelding((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }

  return (
    <form onSubmit={bewaar} className="kaart mt-4 space-y-5 p-6 sm:p-8">
      <h2 className="text-2xl">Je gegevens</h2>
      <p className="-mt-3 text-sm text-inkt-zacht">Je e-mailadres ({stand.email}) wijzigen? Mail dan even de penningmeester.</p>
      <Veld etiket={bedrijf ? 'Contactpersoon' : 'Naam'}>
        <input className="veld" value={invoer.naam} onChange={(e) => zetInvoer({ ...invoer, naam: e.target.value })} />
      </Veld>
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-goud-500" checked={invoer.toon_op_muur} onChange={(e) => zetInvoer({ ...invoer, toon_op_muur: e.target.checked })} />
        <span className="text-sm">Met naam op de muur (anders als “{bedrijf ? 'Een bedrijf' : 'Een held'}”)</span>
      </label>
      <Veld etiket={bedrijf ? 'Bedrijfsnaam' : 'Naam op de muur'}>
        <input className="veld" maxLength={80} value={invoer.weergavenaam} onChange={(e) => zetInvoer({ ...invoer, weergavenaam: e.target.value })} />
      </Veld>
      <Veld etiket="Eén regel eronder">
        <input className="veld" maxLength={80} value={invoer.regel} onChange={(e) => zetInvoer({ ...invoer, regel: e.target.value })} />
      </Veld>
      {bedrijf && (
        <>
          <Veld etiket="Website">
            <input className="veld" value={invoer.website} onChange={(e) => zetInvoer({ ...invoer, website: e.target.value })} />
          </Veld>
          <Logokiezer logo={logo ?? null} huidigeUrl={logo === undefined && stand.heeft_logo ? `/api/logo/${stand.id}?t=${Date.now()}` : null} onChange={zetLogo} />
        </>
      )}
      <Veld etiket="Telefoon">
        <input className="veld" type="tel" value={invoer.telefoon} onChange={(e) => zetInvoer({ ...invoer, telefoon: e.target.value })} />
      </Veld>
      <Veld etiket="Totale jaarbijdrage" hulp={`Het hele bedrag per jaar. Leeg = het tarief van ${alsEuro(stand.tarief_cent)}. Meer mag, tot ${alsEuro(MAXIMUM_INCASSO_CENT)}; dat bedrag wordt dan elk jaar afgeschreven.`} fout={bedragFout}>
        <div className="flex items-center gap-2">
          <span className="text-inkt-zacht">€</span>
          <input className="veld max-w-[10rem]" inputMode="decimal" value={bedragTekst} aria-invalid={Boolean(bedragFout)} onChange={(e) => zetBedragTekst(e.target.value)} />
        </div>
      </Veld>
      <div className="flex items-center gap-4">
        <Knop type="submit" soort="stil" bezig={bezig}>
          Opslaan
        </Knop>
        {melding && <span className="text-sm text-inkt-zacht">{melding}</span>}
      </div>
    </form>
  );
}
